import { DateTime, Interval } from "luxon";
import axiosClient from "../common/axios/axiosConfig";
import { IScheduleFilters } from "../common/interfaces/ScheduleFilters";
import { transformDateToType } from "../utils/date-util/dateUtils";
import { showToast } from "../utils/showToast";

export type ScheduleSituation =
	| "Finalizado"
	| "Agendado"
	| "Confirmado"
	| "Esperando"
	| "Ncompareceu"
	| "Cancelado"
	| "Reagendado"
	| "Em atendimento"
	| "SalaDisponivel";
export interface IMySchedule {
	id: string;
	profissional: string; // idProfissional;
	nomeProfissional: string;
	codu: number;
	imagem: string; // imagem do profissional;
	especialidade: {
		id: string;
		description: string;
	};
	conselho: string[];
	codigoConselho: string[];
	ufConselho: string[];
	horaAtendimento: string;
	status: ScheduleSituation;
	descricaoProcedimento: string;
	idProcedimento: string;
	nomeConsultorio: string;
	telefoneConsultorio: string;
	codigoIbge: string;
	anexos: unknown[];
	avaliado: boolean;
	motivoCancelamento: string;
	linkTelemedicine: string;
	fromClinic?: boolean;
}

type ScheduleType = "Agendado" | "Cancelado" | "Realizado";
export interface IMyScheduleList {
	tipo: ScheduleType;
	agendamentos: IMySchedule[];
}

const scheduleTypes = {
	PENDENTE: "Agendado",
	FINALIZADO: "Realizado",
	CANCELADO: "Cancelado",
} as const;

export const getMySchedules = async (filters: IScheduleFilters): Promise<IMyScheduleList[]> => {
	try {
		if (filters.status) {
			const response = await axiosClient.get<IMySchedule[]>("/historico-agendamentos", {
				params: {
					status: filters.status,
					order: -1,
				},
			});

			return [
				{
					tipo: scheduleTypes[filters.status],
					agendamentos: filters.status === "PENDENTE" ? response.data.sort(sortNearest) : response.data,
				},
			];
		}

		const myScheduleList: IMyScheduleList[] = [];

		// AGENDAMENTOS PENDENTES
		const agendados = await axiosClient.get<IMySchedule[]>("/historico-agendamentos", {
			params: {
				status: "PENDENTE",
				order: 1,
			},
		});
		if (agendados.data.length > 0) {
			myScheduleList.push({
				tipo: "Agendado",
				agendamentos: agendados.data.sort(sortNearest),
			});
		}

		// AGENDAMENTOS REALIZADOS
		const realizados = await axiosClient.get<IMySchedule[]>("/historico-agendamentos", {
			params: {
				status: "FINALIZADO",
				order: -1,
			},
		});
		if (realizados.data.length > 0) {
			myScheduleList.push({
				tipo: "Realizado",
				agendamentos: realizados.data,
			});
		}

		// AGENDAMENTOS CANCELADOS
		const cancelados = await axiosClient.get<IMySchedule[]>("/historico-agendamentos", {
			params: {
				status: "CANCELADO",
				order: -1,
			},
		});
		if (cancelados.data.length > 0) {
			myScheduleList.push({
				tipo: "Cancelado",
				agendamentos: cancelados.data,
			});
		}

		return myScheduleList;
	} catch {
		showToast("error", {
			text: "Erro ao buscar pelos agendamentos",
			confirmButtonText: "OK",
			showConfirmButton: true,
		});
		return [] as IMyScheduleList[];
	}
};

// Ordenar pelos horários mais próximos da data atual
const sortNearest = (a: IMySchedule, b: IMySchedule): number => {
	const dateA = transformDateToType(a.horaAtendimento);
	const dateB = transformDateToType(b.horaAtendimento);
	const today = DateTime.now();

	const intervalA = Interval.fromDateTimes(today, dateA);
	const intervalB = Interval.fromDateTimes(today, dateB);

	// Caso forem do mesmo dia, ordenar pelo menor horário
	if (dateA.day === today.day && dateB.day === today.day) {
		if (dateA < dateB) return -1;

		if (dateA > dateB) return 1;

		return 0;
	}

	if (intervalA.length("milliseconds") < intervalB.length("milliseconds")) return -1;

	if (intervalA.length("milliseconds") > intervalB.length("milliseconds")) return 1;

	return 0;
};
